import React, { useEffect } from 'react';
import HeroSection from '../components/Home/HeroSection';
import ServicesSection from '../components/Home/ServicesSection';
import TestimonialsSection from '../components/Home/TestimonialsSection';
import AppointmentSection from '../components/Home/AppointmentSection';
import styled from 'styled-components';

export interface HomePageProps {
  website: {
    salonInfo: {
      name: string;
      logo?: string;
      phone: string;
      _id?: string;
      city?: string;
      district?: string;
      address?: string;
      workingHours?: any;
    };
    content: {
      salonSlogan: string;
      shortDescription: string;
      serviceDescription?: string;
      servicesDescription?: string; // Her iki formatta da destek için eklendi
      bookingEnabled: boolean;
    };
    testimonials?: Array<{
      id: string;
      name: string;
      rating: number;
      comment: string;
      service: string;
      date: string;
    }>;
    services?: Array<{
      name: string;
      description?: string;
      price?: number;
    }>;
  };
}

const HomePage: React.FC<HomePageProps> = ({ website }) => {
  useEffect(() => {
    // SEO Meta Tags Güncelleme
    const updateSEO = () => {
      const salonName = website.salonInfo.name;
      const location = website.salonInfo.city && website.salonInfo.district 
        ? `${website.salonInfo.city} ${website.salonInfo.district}` 
        : website.salonInfo.city || website.salonInfo.district || '';
      
      // Page Title
      document.title = `${salonName}${location ? ` - ${location}` : ''} | Güzellik ve Bakım Merkezi`;
      
      // Meta Description
      const description = website.content.shortDescription || 
        `${salonName}${location ? ` ${location}'da` : ''} profesyonel güzellik ve bakım hizmetleri. ${website.content.salonSlogan || 'Kaliteli hizmet garantisi.'}`;
      
      updateMetaTag('description', description);
      
      // Keywords
      const keywords = [
        salonName,
        'kuaför',
        'güzellik salonu',
        'berber',
        'estetik',
        'bakım',
        location,
        website.salonInfo.city,
        website.salonInfo.district,
        ...(website.services?.slice(0, 5).map(s => s.name) || [])
      ].filter(Boolean).join(', ');
      
      updateMetaTag('keywords', keywords);
      
      // Open Graph Tags
      updateMetaTag('og:title', document.title, 'property');
      updateMetaTag('og:description', description, 'property');
      updateMetaTag('og:type', 'business.business', 'property');
      updateMetaTag('og:url', window.location.href, 'property');
      
      if (website.salonInfo.logo) {
        updateMetaTag('og:image', website.salonInfo.logo, 'property');
      }
      
      // Twitter Card
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:title', document.title);
      updateMetaTag('twitter:description', description);
      
      if (website.salonInfo.logo) {
        updateMetaTag('twitter:image', website.salonInfo.logo);
      }
      
      // Local Business Tags
      if (website.salonInfo.city) {
        updateMetaTag('geo.region', website.salonInfo.city);
      }
      if (website.salonInfo.district) {
        updateMetaTag('geo.placename', website.salonInfo.district);
      }
      
      // Structured Data - Local Business
      addStructuredData();
    };

    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const addStructuredData = () => {
      // Mevcut structured data varsa kaldır
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "BeautySalon",
        "name": website.salonInfo.name,
        "description": website.content.shortDescription || website.content.salonSlogan,
        "url": window.location.href,
        "logo": website.salonInfo.logo,
        "image": website.salonInfo.logo,
        "telephone": website.salonInfo.phone,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": website.salonInfo.address,
          "addressLocality": website.salonInfo.district,
          "addressRegion": website.salonInfo.city,
          "addressCountry": "TR"
        },
        "openingHours": website.salonInfo.workingHours ? Object.entries(website.salonInfo.workingHours).map(([day, hours]: [string, any]) => 
          hours.isOpen ? `${getDayName(day)} ${hours.start}-${hours.end}` : null
        ).filter(Boolean) : [],
        "priceRange": "$$",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": website.testimonials?.length.toString() || "1"
        },
        "hasOfferCatalog": website.services ? {
          "@type": "OfferCatalog",
          "name": "Güzellik Hizmetleri",
          "itemListElement": website.services.map((service, index) => ({
            "@type": "Offer",
            "position": index + 1,
            "name": service.name,
            "description": service.description,
            "price": service.price,
            "priceCurrency": "TRY"
          }))
        } : undefined
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    };

    const getDayName = (day: string): string => {
      const days: { [key: string]: string } = {
        'monday': 'Mo',
        'tuesday': 'Tu', 
        'wednesday': 'We',
        'thursday': 'Th',
        'friday': 'Fr',
        'saturday': 'Sa',
        'sunday': 'Su'
      };
      return days[day] || day;
    };

    updateSEO();
    
    // Content nesnesini ve servicesDescription/serviceDescription değerlerini kontrol et
    console.log('HomePage - content nesnesi:', website.content);
    console.log('HomePage - serviceDescription:', website.content.serviceDescription);
    console.log('HomePage - servicesDescription:', website.content.servicesDescription);
    console.log('HomePage - testimonials:', website.testimonials);
  }, [website]);

  // servicesDescription veya serviceDescription'dan hangisi varsa onu kullan
  const getServiceDescription = () => {
    if (website.content.servicesDescription) {
      return website.content.servicesDescription;
    } else if (website.content.serviceDescription) {
      return website.content.serviceDescription;
    }
    return "Salonumuzun sunduğu hizmetleri keşfedin."; // Varsayılan değer
  };

  // Testimonials'ı kontrol et
  const testimonials = website.testimonials || [];

  return (
    <HomePageContainer>
      <HeroSection 
        salonName={website.salonInfo.name}
        slogan={website.content.salonSlogan}
        description={website.content.shortDescription}
        logo={website.salonInfo.logo}
        salonId={website.salonInfo._id}
      />
      <ServicesSection
        description={getServiceDescription()}
        salonId={website.salonInfo._id}
      />
      {testimonials.length > 0 && (
        <TestimonialsSection testimonials={testimonials} />
      )}
      <AppointmentSection
        salonInfo={website.salonInfo}
        bookingEnabled={website.content.bookingEnabled}
      />
    </HomePageContainer>
  );
};

const HomePageContainer = styled.div`
  /* Ana sayfa container stil özellikleri */
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export default HomePage; 